import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  LocationOn,
  AccessTime,
  Euro,
  People,
  Person,
  Schedule,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from '../config/axios';

const Reservables = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [reservables, setReservables] = useState([]);
  const [filteredReservables, setFilteredReservables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtri
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    location: '',
    availability: '',
  });

  // Carica i reservables
  useEffect(() => {
    const fetchReservables = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/reservables');
        setReservables(response.data);
        setFilteredReservables(response.data);
      } catch (err) {
        console.error('Error fetching reservables:', err);
        setError('Errore nel caricamento delle prenotazioni');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchReservables();
    }
  }, [isAuthenticated]);

  // Applica i filtri
  useEffect(() => {
    let filtered = [...reservables];

    // Filtro per nome/descrizione
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(reservable =>
        reservable.name.toLowerCase().includes(searchLower) ||
        (reservable.description && reservable.description.toLowerCase().includes(searchLower))
      );
    }

    // Filtro per categoria
    if (filters.category) {
      filtered = filtered.filter(reservable => reservable.category === filters.category);
    }

    // Filtro per location
    if (filters.location) {
      filtered = filtered.filter(reservable =>
        reservable.location && reservable.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Filtro per disponibilità (giorno della settimana)
    if (filters.availability) {
      const dayOfWeek = parseInt(filters.availability);
      filtered = filtered.filter(reservable =>
        reservable.availability.some(avail => avail.dayOfWeek === dayOfWeek)
      );
    }

    setFilteredReservables(filtered);
  }, [reservables, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      location: '',
      availability: '',
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      sala: 'Sala',
      istruttore: 'Istruttore',
      attrezzatura: 'Attrezzatura'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      sala: 'primary',
      istruttore: 'primary',
      attrezzatura: 'primary'
    };
    return colors[category] || 'default';
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    return days[dayOfWeek] || '';
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">
          Devi essere loggato per visualizzare le prenotazioni.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Prenota
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Scegli tra le risorse disponibili per la prenotazione
      </Typography>

      {/* Filtri */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Filtri di Ricerca
          </Typography>
          <Button 
            variant="outlined" 
            onClick={clearFilters}
            sx={{ 
              minWidth: 120,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'primary.light',
                color: 'primary.dark',
              }
            }}
          >
            Cancella filtri
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Cerca per nome o descrizione"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            variant="outlined"
            size="large"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                fontSize: '1.1rem',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              },
            }}
          />

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel sx={{ color: 'text.secondary' }}>Categoria</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                label="Categoria"
                size="large"
                sx={{
                  backgroundColor: 'white',
                  fontSize: '1.1rem',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">Tutte le categorie</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="sala">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Sala" color="primary" size="small" />
                  </Box>
                </MenuItem>
                <MenuItem value="istruttore">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Istruttore" color="secondary" size="small" />
                  </Box>
                </MenuItem>
                <MenuItem value="attrezzatura">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Attrezzatura" color="success" size="small" />
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              sx={{ minWidth: 200, flex: 1 }}
              label="Location o area"
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              variant="outlined"
              size="large"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn color="primary" />
                  </InputAdornment>
                ),
              }}
            //   sx={{
            //     '& .MuiOutlinedInput-root': {
            //       backgroundColor: 'white',
            //       fontSize: '1.1rem',
            //       '&:hover': {
            //         '& .MuiOutlinedInput-notchedOutline': {
            //           borderColor: 'primary.main',
            //         },
            //       },
            //     },
            //   }}
            />

            <FormControl sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel sx={{ color: 'text.secondary' }}>Giorno disponibilità</InputLabel>
              <Select
                value={filters.availability}
                onChange={(e) => handleFilterChange('availability', e.target.value)}
                label="Giorno disponibilità"
                size="large"
                sx={{
                  backgroundColor: 'white',
                  fontSize: '1.1rem',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime color="action" />
                    <Typography variant="body2" color="text.secondary">Tutti i giorni</Typography>
                  </Box>
                </MenuItem>
                {[1, 2, 3, 4, 5, 6, 0].map(day => (
                  <MenuItem key={day} value={day}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTime color="action" />
                      <Typography variant="body2">{getDayName(day)}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Filtri attivi */}
        {(filters.search || filters.category || filters.location || filters.availability) && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Filtri attivi:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {filters.search && (
                <Chip
                  label={`Ricerca: "${filters.search}"`}
                  onDelete={() => handleFilterChange('search', '')}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
              {filters.category && (
                <Chip
                  label={`Categoria: ${getCategoryLabel(filters.category)}`}
                  onDelete={() => handleFilterChange('category', '')}
                  color="secondary"
                  variant="outlined"
                  size="small"
                />
              )}
              {filters.location && (
                <Chip
                  label={`Location: "${filters.location}"`}
                  onDelete={() => handleFilterChange('location', '')}
                  color="info"
                  variant="outlined"
                  size="small"
                />
              )}
              {filters.availability && (
                <Chip
                  label={`Giorno: ${getDayName(parseInt(filters.availability))}`}
                  onDelete={() => handleFilterChange('availability', '')}
                  color="warning"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Risultati */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        p: 2,
        backgroundColor: 'primary.light',
        borderRadius: 2,
        color: 'primary.contrastText',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            📋 Risultati
          </Typography>
          <Chip 
            label={`${filteredReservables.length} risors${filteredReservables.length === 1 ? 'a' : 'e'} trovat${filteredReservables.length === 1 ? 'a' : 'e'}`}
            color="primary"
            variant="filled"
            sx={{ 
              backgroundColor: 'white',
              color: 'primary.main',
              fontWeight: 600
            }}
          />
        </Box>
        {filteredReservables.length > 0 && (
          <Typography 
            variant="body2" 
            sx={{ 
              opacity: 0.9,
              textAlign: { xs: 'center', sm: 'right' }
            }}
          >
            Clicca su "Prenota" per procedere con la prenotazione
          </Typography>
        )}
      </Box>

      {filteredReservables.length === 0 ? (
        <Paper 
          elevation={1}
          sx={{ 
            p: 4, 
            textAlign: 'center',
            backgroundColor: 'grey.50',
            border: '2px dashed',
            borderColor: 'grey.300'
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Search sx={{ fontSize: 48, color: 'grey.400' }} />
          </Box>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nessuna risorsa trovata
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Prova a modificare i filtri di ricerca o a rimuoverne alcuni per vedere più risultati.
          </Typography>
          <Button 
            variant="outlined" 
            onClick={clearFilters}
            startIcon={<Search />}
            sx={{ mt: 1 }}
          >
            Cancella tutti i filtri
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3} alignItems="stretch" justifyContent="center">
          {filteredReservables.map((reservable) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={reservable._id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column'
              }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={getCategoryLabel(reservable.category)}
                      color={getCategoryColor(reservable.category)}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {reservable.requiresApproval && (
                      <Chip
                        label="Richiede approvazione"
                        color="warning"
                        size="small"
                        icon={<Schedule />}
                      />
                    )}
                  </Box>

                  <Typography variant="h6" component="h2" gutterBottom>
                    {reservable.name}
                  </Typography>

                  {reservable.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {reservable.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationOn sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {reservable.location || 'Non specificata'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <People sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Capacità: {reservable.capacity} persona{reservable.capacity === 1 ? '' : 'e'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Euro sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {reservable.costPerHour}€/ora
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTime sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Durate: {reservable.availableDurations.map(d => formatDuration(d)).join(', ')}
                    </Typography>
                  </Box>

                  {reservable.teacher && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Person sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Istruttore: {reservable.teacher.name} {reservable.teacher.surname}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Disponibilità:
                  </Typography>
                  
                  {reservable.availability.map((avail, index) => (
                    <Chip
                      key={index}
                      maxWidth="50%"
                      label={`${getDayName(avail.dayOfWeek)} ${avail.startTime}-${avail.endTime}`}
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                  
                </CardContent>

                <CardActions>
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={!reservable.isActive}
                    startIcon={reservable.isActive ? <CheckCircle /> : <Cancel />}
                    onClick={() => {
                      if (reservable.isActive) {
                        navigate(`/reservables/${reservable._id}`);
                      }
                    }}
                  >
                    {reservable.isActive ? 'Prenota' : 'Non disponibile'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default Reservables;
